import * as React from "react";
import {useEffect, useState} from "react";
import PropTypes from "prop-types";
import {useTheme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
  Card,
  Button,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Snackbar,
  TextField,
  FormHelperText
} from "@mui/material";
import {GET, POST} from "APIclient";
import properties from "constants/ApplicationConstants";
import MDButton from "components/MDButton";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {useNavigate} from "react-router-dom";
import {formatDesignation} from "constants/ApplicationConstants";

export default function CreatePerformanceImprovement() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const [employeeId, setEmployeeId] = useState("");
  const [employeeList, setEmployeeList] = useState([]);
  const [searchResult, setSearchResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [hrManagers, setHrManagers] = useState([]);
  const [open, setOpen] = useState(false);
  const [snakColor, setSnakColor] = useState("");
  const [snakMessage, setSnakMessage] = useState("");
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  const handleClose = () => {
    setOpen(false);
  };

  const [pipData, setPipData] = useState({
    startDate: "",
    endDate: "",
    days: "",
    reason: "",
    issuedBy: ""
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleEmployeeChange = event => {
    const value = event.target.value;
    setEmployeeId(value);
    setErrors(prev => ({
      ...prev,
      employee: value ? "" : "Please select an Employee"
    }));
  };

  const handleBack = () => {
    setSearchResult(null);
    setEmployeeId("");
    setErrors({});
    setPipData({
      startDate: "",
      endDate: "",
      days: "",
      reason: "",
      issuedBy: ""
    });
    setSaveError(null);
    setFormErrors({});
    setIsSaveDisabled(true);
  };

  const formatDate = dateString => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const calculateDays = (start, end) => {
    if (start && end) {
      const startDate = dayjs(start);
      const endDate = dayjs(end);
      const diff = endDate.diff(startDate, "day") + 1;
      return diff >= 0 ? diff.toString() : "";
    }
    return "";
  };

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "startDate":
        if (!value) error = "Start Date is required";
        break;
      case "endDate":
        if (!value) error = "End Date is required";
        break;
      case "days":
        if (!value) error = "Number of Days is required";
        else if (!/^\d+$/.test(value) || parseInt(value) <= 0)
          error = "Number of Days must be a positive number";
        break;
      case "reason":
        if (!value) error = "Reason is required";
        break;
      case "issuedBy":
        if (!value) error = "Issued By is required";
        break;
      default:
        break;
    }
    return error;
  };

  const handlePipChange = field => event => {
    const value = event.target ? event.target.value : event;
    setPipData(prev => {
      const newData = {...prev, [field]: value};

      if ((field === "startDate" || field === "endDate") && newData.startDate && newData.endDate) {
        newData.days = calculateDays(newData.startDate, newData.endDate);
      }
      return newData;
    });

    const error = validateField(field, value);
    setFormErrors(prev => ({...prev, [field]: error}));
  };

  const handleSearch = async () => {
    if (!employeeId) {
      setErrors(prev => ({
        ...prev,
        employee: "Please select an Employee"
      }));
      setOpen(true);
      setSnakColor("error");
      setSnakMessage("Please select an Employee");
      return;
    }

    setIsLoading(true);
    setErrors(prev => ({...prev, employee: ""}));
    setSearchResult(null);

    try {
      const url = `employee/employee-details/${employeeId}`;
      const response = await GET(url);

      if (response?.data && response?.data?.data?.employee) {
        const employee = response?.data?.data?.employee;
        setSearchResult({
          employeeId: employee.empId?.toString() || "",
          firstName: employee.firstName || "",
          middleName: employee.middleName || "",
          lastName: employee.lastName || "",
          joiningDate: formatDate(employee.joinDate) || "",
          designation: formatDesignation(employee.designation || ""),
          personalEmail: employee.emailAddress || "",
          officialEmail: employee.officialEmailAddress || "",
          mobile: employee.mobileNo || ""
        });
      } else {
        setErrors(prev => ({
          ...prev,
          employee: "No employee found with this ID"
        }));
        setOpen(true);
        setSnakColor("error");
        setSnakMessage("No employee found with this ID");
      }
    } catch (err) {
      setErrors(prev => ({
        ...prev,
        employee: "Failed to fetch employee details. Please try again."
      }));
      setOpen(true);
      setSnakColor("error");
      setSnakMessage("Failed to fetch employee details. Please try again.");
      console.error("Error fetching employee details:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    const newErrors = {};
    Object.keys(pipData).forEach(key => {
      const error = validateField(key, pipData[key]);
      if (error) newErrors[key] = error;
    });

    setFormErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setSaveLoading(true);
    setSaveError(null);

    const payload = {
      empId: parseInt(searchResult.employeeId, 10),
      pipStartDate: pipData.startDate,
      pipEndDate: pipData.endDate,
      days: parseInt(pipData.days, 10),
      reason: pipData.reason,
      issuedBy: parseInt(pipData.issuedBy, 10)
    };

    try {
      const response = await POST("admin/performance", payload);
      if (response?.status === 200) {
        setSnakColor("success");
        setSnakMessage("Performance Improvement Program details added successfully.");
        setOpen(true);
        setTimeout(() => {
          navigate("/performanceImprovementList");
        }, 1000);
      }
    } catch (error) {
      const statusCode = error?.response?.status;
      const message =
        error?.response?.data?.message || "Failed to save Performance Improvement Program details.";

      if (statusCode === 400) {
        setSnakColor("error");
        setSnakMessage("Bad request. Please check your inputs.");
      } else if (statusCode === 401) {
        setSnakColor("error");
        setSnakMessage("Unauthorized access. Please log in again.");
      } else if (statusCode === 403) {
        setSnakColor("error");
        setSnakMessage("Forbidden. You don’t have permission to perform this action.");
      } else if (statusCode === 404) {
        setSnakColor("error");
        setSnakMessage("API not found. Please contact support.");
      } else if (statusCode === 409) {
        setSnakColor("error");
        setSnakMessage("Performance Improvement Program already exists.");
      } else if (statusCode === 500) {
        setSnakColor("error");
        setSnakMessage("Server error. Please try again later.");
      } else {
        setSnakColor("error");
        setSnakMessage(message);
      }
      setOpen(true);
    } finally {
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await GET("admin/employeeDetails");
        if (response.status === 200 && Array.isArray(response.data.data)) {
          setEmployeeList(
            response.data.data.map(employee => ({
              empId: employee.empId?.toString() || "",
              firstName: employee.firstName || "N/A",
              lastName: employee.lastName || "N/A"
            }))
          );
        } else {
          setEmployeeList([]);
          setOpen(true);
          setSnakColor("error");
          setSnakMessage("No employees found");
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
        setEmployeeList([]);
        setOpen(true);
        setSnakColor("error");
        setSnakMessage(error.response?.data?.message || "Error fetching employees data");
      }
    };

    const fetchHrManagers = async () => {
      try {
        const response = await GET("admin/hr-managers");
        const data = response?.data?.data;
        setHrManagers(data || []);
      } catch (error) {
        console.error("Error fetching HR managers:", error);
      }
    };

    fetchEmployees();
    fetchHrManagers();
  }, []);

  useEffect(() => {
    const allFieldsFilled = Object.keys(pipData).every(key => pipData[key] !== "");
    const noErrors = Object.keys(formErrors).every(key => !formErrors[key]);
    setIsSaveDisabled(!(allFieldsFilled && noErrors));
  }, [pipData, formErrors]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {searchResult ? (
          <>
            <MDTypography
              variant="h6"
              fontWeight="medium"
              pb={2}
              sx={{fontFamily: properties.fontFamily}}
            >
              Employee Details
            </MDTypography>
            <Card sx={{padding: 3, marginBottom: 3}}>
              <MDBox
                display="grid"
                gridTemplateColumns={{xs: "1fr", sm: "repeat(3, 1fr)"}}
                gap={2}
                mb={2}
              >
                <MDBox display="flex" alignItems="center">
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                  >
                    Employee ID:
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                  >
                    {searchResult.employeeId || "-"}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="center">
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                  >
                    Joining Date:
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                  >
                    {searchResult.joiningDate || "-"}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="center">
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                  >
                    Designation:
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                  >
                    {searchResult.designation || "-"}
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDBox
                display="grid"
                gridTemplateColumns={{xs: "1fr", sm: "repeat(3, 1fr)"}}
                gap={2}
                mb={2}
              >
                <MDBox display="flex" alignItems="center">
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                  >
                    First Name:
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                  >
                    {searchResult.firstName || "-"}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="center">
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                  >
                    Middle Name:
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                  >
                    {searchResult.middleName || "-"}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="center">
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                  >
                    Last Name:
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                  >
                    {searchResult.lastName || "-"}
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDBox
                display="grid"
                gridTemplateColumns={{xs: "1fr", sm: "repeat(3, 1fr)"}}
                gap={2}
                mb={2}
              >
                <MDBox display="flex" alignItems="center">
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                  >
                    Personal Email ID:
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                  >
                    {searchResult.personalEmail || "-"}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="center">
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                  >
                    Official Email ID:
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                  >
                    {searchResult.officialEmail || "-"}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="center">
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                  >
                    Mobile:
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                  >
                    {searchResult.mobile || "-"}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>

            <MDTypography
              variant="h6"
              fontWeight="medium"
              sx={{fontFamily: properties.fontFamily}}
              gutterBottom
            >
              Performance Improvement Program
            </MDTypography>

            <Card sx={{padding: 3}}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box maxWidth={900} mx="auto">
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        format="DD/MM/YYYY"
                        label={
                          <span>
                            PIP Start Date <span style={{color: properties.colors.Red}}>*</span>
                          </span>
                        }
                        value={pipData.startDate ? dayjs(pipData.startDate) : null}
                        onChange={newValue => {
                          const formattedDate = newValue
                            ? dayjs(newValue).format("YYYY-MM-DD")
                            : "";
                          handlePipChange("startDate")({target: {value: formattedDate}});
                        }}
                        slotProps={{
                          textField: {
                            error: !!formErrors.startDate,
                            helperText: formErrors.startDate,
                            fullWidth: true,
                            sx: {"& .MuiInputBase-root": {height: 40}}
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        format="DD/MM/YYYY"
                        label={
                          <span>
                            PIP End Date <span style={{color: properties.colors.Red}}>*</span>
                          </span>
                        }
                        value={pipData.endDate ? dayjs(pipData.endDate) : null}
                        onChange={newValue => {
                          const formattedDate = newValue
                            ? dayjs(newValue).format("YYYY-MM-DD")
                            : "";
                          handlePipChange("endDate")({target: {value: formattedDate}});
                        }}
                        slotProps={{
                          textField: {
                            error: !!formErrors.endDate,
                            helperText: formErrors.endDate,
                            fullWidth: true,
                            sx: {"& .MuiInputBase-root": {height: 40}}
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={
                          <span>
                            Number of Days <span style={{color: properties.colors.Red}}>*</span>
                          </span>
                        }
                        type="text"
                        value={pipData.days}
                        onChange={handlePipChange("days")}
                        fullWidth
                        sx={{"& .MuiInputBase-root": {height: 40}}}
                        error={!!formErrors.days}
                        helperText={formErrors.days}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl
                        fullWidth
                        sx={{"& .MuiInputBase-root": {height: 40}}}
                        error={!!formErrors.issuedBy}
                      >
                        <InputLabel>
                          Issued By <span style={{color: properties.colors.Red}}>*</span>
                        </InputLabel>
                        <Select
                          label="Issued By"
                          value={pipData.issuedBy}
                          onChange={handlePipChange("issuedBy")}
                        >
                          <MenuItem value="">
                            <em>Select</em>
                          </MenuItem>
                          {hrManagers.map(manager => (
                            <MenuItem key={manager.empId} value={manager.empId}>
                              {`${manager.firstName} ${manager.lastName}`}
                            </MenuItem>
                          ))}
                        </Select>
                        {formErrors.issuedBy && (
                          <MDTypography variant="caption" color="error">
                            {formErrors.issuedBy}
                          </MDTypography>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label={
                          <span>
                            Reason <span style={{color: properties.colors.Red}}>*</span>
                          </span>
                        }
                        multiline
                        rows={3}
                        value={pipData.reason}
                        onChange={handlePipChange("reason")}
                        fullWidth
                        sx={{
                          maxWidth: 440,
                          "& .MuiInputBase-root": {minHeight: 80}
                        }}
                        error={!!formErrors.reason}
                        helperText={formErrors.reason}
                      />
                    </Grid>
                  </Grid>

                  {saveError && (
                    <MDTypography color="error" variant="body2" sx={{marginTop: 2}}>
                      {saveError}
                    </MDTypography>
                  )}

                  <MDBox display="flex" justifyContent="flex-end" mt={3} mb={2}>
                    <MDBox sx={{display: "flex", gap: 1}}>
                      <MDButton
                        variant="gradient"
                        color="warning"
                        sx={{width: "20ch", fontFamily: properties.fontFamily}}
                        onClick={handleBack}
                      >
                        Back
                      </MDButton>
                      <MDButton
                        variant="gradient"
                        color="info"
                        sx={{
                          width: "20ch",
                          fontFamily: properties.fontFamily,
                          background: properties.colors.bgcolorbluethem
                        }}
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                      >
                        Save
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </Box>
              </LocalizationProvider>
            </Card>
          </>
        ) : (
          <>
            <MDTypography
              sx={{
                color: "textPrimary",
                fontFamily: properties.fontFamily,
                fontSize: "16px",
                fontWeight: "bold"
              }}
              gutterBottom
            >
              Performance Improvement Program
            </MDTypography>
            <Box display="flex" justifyContent="start" mt={3}>
              <Card sx={{padding: 3, width: 400}}>
                <MDTypography
                  sx={{
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "14px",
                    fontWeight: "bold"
                  }}
                  gutterBottom
                >
                  Select Employee
                </MDTypography>
                <MDBox pt={3} px={3}>
                  <FormControl variant="standard" sx={{width: "290px"}} error={!!errors.employee}>
                    <InputLabel id="employee-select">
                      Select Employee <span style={{color: properties.colors.Red}}>*</span>
                    </InputLabel>
                    <Select
                      labelId="employee-select"
                      id="employee-select"
                      value={employeeId}
                      onChange={handleEmployeeChange}
                      name="employee"
                      label="Employee"
                    >
                      <MenuItem value="">
                        <em>Select Employee</em>
                      </MenuItem>
                      {employeeList.length > 0 ? (
                        employeeList.map(employee => (
                          <MenuItem key={employee.empId} value={employee.empId}>
                            {employee.firstName} {employee.lastName} [{employee.empId}]
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>No employees available</MenuItem>
                      )}
                    </Select>
                    {errors.employee && <FormHelperText>{errors.employee}</FormHelperText>}
                  </FormControl>
                </MDBox>
                <MDButton
                  sx={{
                    gridColumn: "span 2",
                    display: "block",
                    marginLeft: "auto",
                    marginTop: "6px",
                    background: properties.colors.bgcolorbluethem,
                    color: isLoading ? "inherit" : "white"
                  }}
                  variant="contained"
                  color="info"
                  onClick={handleSearch}
                  disabled={isLoading}
                >
                  Search
                </MDButton>
              </Card>
            </Box>
          </>
        )}

        <Snackbar
          open={open}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
          autoHideDuration={6000}
          onClose={handleClose}
        >
          <Alert
            onClose={handleClose}
            severity={snakColor === "success" ? "success" : "error"}
            variant="filled"
            sx={{width: "100%", color: "#fff"}}
          >
            {snakMessage}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
}
